<template>
  <!-- Table Container Card -->
  <b-card no-body>
    <div class="m-2">
      <b-row class="d-flex align-items-center">
        <b-col
          md="6"
          class=""
        >
          <validation-observer ref="batchTransactionPostingRules">
            <b-form>
              <b-col
                md="12"
                class="mb-2"
              >
                <div class="mb-1">
                  {{ $t('Select file (.xlsx format only)') }}
                </div>
                <b-form-file
                  id="file-name"
                  v-model="hasFileData"
                  :label="$t('Select File (.xlsx format only)')"
                  :state="Boolean(hasFileData)"
                  :placeholder="$t('Choose a file or drop it here...')"
                  :drop-placeholder="$t('Drop file here...')"
                  error-name="file"
                  rules="required"
                  accept=".xlsx"
                  @change="onChangeXLSX($event)"
                />
              </b-col>
            </b-form>
          </validation-observer>
        </b-col>

        <b-col
          md="6"
        >
          <b-row>
            <b-col
              md="5"
            >
              <Button
                v-if="getUserPermission('REVERSAL_POSTING')"
                class="mt-1"
                :label="$t('Submit Transaction')"
                size="md"
                :loading="isLoading"
                :disabled="isDisabled"
                @click="handleCreate"
              />
            </b-col>
            <b-col
              md="7"
            >
              <Button
                class="ml-lg-2 mt-1"
                align="end"
                :label="$t('Download Batch Reversal Transaction Format')"
                size="md"
                icon="DownloadIcon"
                show-icon
                :loading="isLoading2"
                :disabled="isDisabled2"
                @click="handleDownloadSubmit"
              />
            </b-col>
          </b-row>
        </b-col>
      </b-row>
    </div>
    <b-table
      v-if="errorMessages.length"
      :items="errorMessages"
      responsive
      :fields="translatedErrorHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    />
    <b-table
      v-if="!errorMessages.length"
      ref="reversalBatchTransactionPosting"
      :items="parseExcelFile"
      responsive
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>
    </b-table>
  </b-card>
</template>

<script>
import {
  BCard, BRow, BCol, BForm, BTable, BFormFile,
} from 'bootstrap-vue'
import { required } from '@validations'
import { ValidationObserver } from 'vee-validate'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { mapActions, mapGetters } from 'vuex'
import { permissionMixin } from '@core/mixins/permission'
import XLSX from 'xlsx'
import { toAmount } from '@/helpers/formatAmount'
import Button from '../../../components/button/Button.vue'
import { batchAccountFormat, tableLessHeaders, tableAllHeaders } from '../../../../helpers/data'

export default {
  components: {
    BCard,
    BTable,
    BRow,
    BCol,
    BForm,
    BFormFile,
    ValidationObserver,
    Button,
  },
  filters: {
    capitalize(str) {
      return str.charAt(0).toUpperCase() + str.slice(1)
    },
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      toAmount,
      fileData: [],
      hasFileData: [],
      parseExcelFile: [],
      validatedExcelFileData: [],
      sortOrders: {},
      accountFormat: batchAccountFormat,
      successHeaders: tableAllHeaders,
      failedHeaders: tableLessHeaders,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'referenceNumber', sortable: true, label: 'Reference Number' },
        { key: 'instrumentNumber', sortable: true, label: 'Instrument Number' },
      ],
      required,
      isDisabled: true,
      isLoading: false,
      isDisabled2: false,
      isLoading2: false,
      showFormError: false,
      sortDesc: false,
      sortBy: 'lastName',
      errorHeaders: [
        { key: 'referenceNumber', sortable: true, label: 'Reference Number' },
        { key: 'message', sortable: true, label: 'Error Messages' },
      ],
      errorMessages: [],
    }
  },
  computed: {
    ...mapGetters({
      userDetails: 'auth/getCurrentUser',
    }),
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedErrorHeaders() {
      return this.errorHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
  },
  methods: {
    ...mapActions({
      reversalBatchTransactionPosting: 'posting/reversalBatchTransactionPosting',
      downloadAccountFormat: 'batch/downloadAccountFormat',
    }),
    async handleCreate() {
      this.$refs.batchTransactionPostingRules.validate().then(success => {
        if (success) {
          this.confirm(this.reversalBatchTransactionPostingAction, { text: this.$t('Are you sure you want to upload a batch reversal transaction?') })
        }
      })
    },
    async reversalBatchTransactionPostingAction() {
      try {
        this.showFormError = false
        this.isDisabled = true
        this.isLoading = true
        const formData = this.parseExcelFile.map(item => ({
          instrumentNumber: item.instrumentNumber,
          referenceNumber: item.referenceNumber,
          initiatedBy: this.userDetails.email,
        }))
        const response = await this.reversalBatchTransactionPosting({ payload: formData })
        const { status, data } = response
        if (status === 200 && data === true) {
          this.hasFileData = []
          this.parseExcelFile = []
          this.alertSuccess({ text: this.$t('Your Batch reversal transaction request was successful.') })
        } else if (status === 200 && data === false) {
          this.alertError({ text: this.$t('Your Batch reversal transaction request failed.') })
        }
      } catch (error) {
        const failedText = this.$t('Your Batch reversal transaction request failed. Reason:')
        error.response.data.errors.forEach(responseError => {
          this.alertError({ text: `${failedText} ${responseError.errorMessage}` })
        })
        this.showFormError = true
      } finally {
        this.isDisabled = false
        this.isLoading = false
      }
    },
    onChangeXLSX(event) {
      this.isDisabled = false
      this.fileData = event.target.files ? event.target.files[0] : null
      if (this.fileData) {
        const vm = this
        const reader = new FileReader()
        reader.onload = e => {
          const data = e.target.result
          const workbook = XLSX.read(data, { type: 'binary' })
          const sheetName = workbook.SheetNames[0]
          const worksheet = workbook.Sheets[sheetName]
          const rowObject = XLSX.utils.sheet_to_row_object_array(worksheet, { raw: false })
          vm.parseExcelFile = JSON.parse(JSON.stringify(rowObject)).map(items => ({
            referenceNumber: items['Reference Number'],
            instrumentNumber: items['Instrument Number'],
          }))
        }
        reader.readAsBinaryString(this.fileData)
      }
    },
    async handleDownloadSubmit() {
      this.confirm(this.downloadAction, { text: this.$t('Are you sure you want to download a batch reversal transaction format?') })
    },
    async downloadAction() {
      try {
        this.isLoading2 = true
        this.isDisabled2 = true
        const BatchAccountFileTemplate = 17
        const response = await this.downloadAccountFormat(BatchAccountFileTemplate)
        if (response.status === 200) {
          const downloadAnchor = document.createElement('a')
          downloadAnchor.setAttribute('href', response.data.data)
          const filename = 'Udara360-Batch-Reversal-Transaction.xlsx'
          downloadAnchor.setAttribute('download', filename)
          document.body.appendChild(downloadAnchor)
          downloadAnchor.click()
          document.body.removeChild(downloadAnchor)
          this.alertSuccess({ text: this.$t('Your request to download a batch reversal transaction format was successful') })
        }
      } catch (error) {
        const batchFailed = this.$t('Your request to download a batch reversal transaction format failed. Reason:')
        error.response.data.errors.forEach(responseError => {
          this.alertError({ text: `${batchFailed} ${responseError.errorMessage}` })
          this.isLoading2 = false
          this.isDisabled2 = false
        })
      } finally {
        this.isLoading2 = false
        this.isDisabled2 = false
      }
    },
  },
}
</script>
